/**
 * Device Fingerprint Utility
 * 
 * Generates a unique device identifier using browser characteristics.
 * Used to prevent multiple spins from the same device.
 */

const STORAGE_KEYS = {
  FINGERPRINT: 'cc_device_fp',
  SPIN_COMPLETED: 'cc_spin_completed',
  SPIN_COUPON: 'cc_spin_coupon',
  SPIN_OFFER: 'cc_spin_offer',
  SPIN_PLAN_CATEGORY: 'cc_spin_plan_category',
  SPIN_PLAN_CATEGORY_DISPLAY: 'cc_spin_plan_category_display',
  SPIN_PLAN_ID: 'cc_spin_plan_id',
};

/**
 * Generate a simple hash from a string
 * @param {string} str - String to hash
 * @returns {string} - Hash string
 */
const simpleHash = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
};

/**
 * Get canvas fingerprint
 * @returns {string} - Canvas fingerprint
 */
const getCanvasFingerprint = () => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 200;
    canvas.height = 50;
    
    // Draw text with specific styling
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('ClimbCapital', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('SpinToWin', 4, 17);
    
    return simpleHash(canvas.toDataURL());
  } catch (e) {
    return 'canvas-error';
  }
};

/**
 * Get WebGL fingerprint
 * @returns {string} - WebGL fingerprint
 */
const getWebGLFingerprint = () => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return 'no-webgl';
    
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (debugInfo) {
      const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
      const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
      return simpleHash(vendor + renderer);
    }
    return 'webgl-basic';
  } catch (e) {
    return 'webgl-error';
  }
};

/**
 * Get browser/device characteristics
 * @returns {Object} - Device characteristics
 */
const getDeviceCharacteristics = () => {
  const nav = window.navigator;
  const screen = window.screen;
  
  return {
    userAgent: nav.userAgent,
    language: nav.language,
    platform: nav.platform,
    hardwareConcurrency: nav.hardwareConcurrency || 'unknown',
    deviceMemory: nav.deviceMemory || 'unknown',
    screenResolution: `${screen.width}x${screen.height}`,
    screenColorDepth: screen.colorDepth,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezoneOffset: new Date().getTimezoneOffset(),
    touchSupport: 'ontouchstart' in window,
    cookiesEnabled: nav.cookieEnabled,
  };
};

/**
 * Generate device fingerprint
 * @returns {string} - Unique device fingerprint
 */
const generateFingerprint = () => {
  const characteristics = getDeviceCharacteristics();
  const canvasFp = getCanvasFingerprint();
  const webglFp = getWebGLFingerprint();
  
  // Combine all characteristics
  const combined = JSON.stringify({
    ...characteristics,
    canvas: canvasFp,
    webgl: webglFp,
  });
  
  // Generate final fingerprint
  const fingerprint = simpleHash(combined);
  const timestamp = Date.now().toString(36);
  
  return `${fingerprint}-${canvasFp}-${webglFp}-${timestamp}`;
};

/**
 * Get or generate device fingerprint
 * @returns {string} - Device fingerprint
 */
export const getDeviceFingerprint = () => {
  try {
    // Check localStorage first
    const stored = localStorage.getItem(STORAGE_KEYS.FINGERPRINT);
    if (stored) {
      return stored;
    }
    
    // Generate new fingerprint
    const fingerprint = generateFingerprint();
    localStorage.setItem(STORAGE_KEYS.FINGERPRINT, fingerprint);
    return fingerprint;
  } catch (e) {
    // Fallback if localStorage is not available
    return generateFingerprint();
  }
};

/**
 * Check if user has already spun (from localStorage)
 * @returns {boolean} - Whether user has spun
 */
export const hasSpunLocally = () => {
  try {
    return localStorage.getItem(STORAGE_KEYS.SPIN_COMPLETED) === 'true';
  } catch (e) {
    return false;
  }
};

/**
 * Get stored spin result
 * @returns {Object|null} - Stored spin result or null
 */
export const getStoredSpinResult = () => {
  try {
    if (!hasSpunLocally()) return null;
    
    const storedPlanId = localStorage.getItem(STORAGE_KEYS.SPIN_PLAN_ID);

    return {
      couponCode: localStorage.getItem(STORAGE_KEYS.SPIN_COUPON),
      offerName: localStorage.getItem(STORAGE_KEYS.SPIN_OFFER),
      planCategory: localStorage.getItem(STORAGE_KEYS.SPIN_PLAN_CATEGORY),
      planCategoryDisplay: localStorage.getItem(STORAGE_KEYS.SPIN_PLAN_CATEGORY_DISPLAY),
      planId: storedPlanId ? Number(storedPlanId) : null,
    };
  } catch (e) {
    return null;
  }
};

/**
 * Mark spin as completed and store result
 * @param {string} couponCode - Won coupon code
 * @param {string} offerName - Won offer name
 * @param {string} planCategory - Plan category (stock_recommendations, live_corporate_announcements, all)
 * @param {string} planCategoryDisplay - Human-readable plan category display name
 * @param {number|string|null} planId - ID of the plan the coupon is tied to (if any)
 */
export const markSpinCompleted = (couponCode, offerName, planCategory = '', planCategoryDisplay = '', planId = null) => {
  try {
    localStorage.setItem(STORAGE_KEYS.SPIN_COMPLETED, 'true');
    localStorage.setItem(STORAGE_KEYS.SPIN_COUPON, couponCode);
    localStorage.setItem(STORAGE_KEYS.SPIN_OFFER, offerName);
    localStorage.setItem(STORAGE_KEYS.SPIN_PLAN_CATEGORY, planCategory);
    localStorage.setItem(STORAGE_KEYS.SPIN_PLAN_CATEGORY_DISPLAY, planCategoryDisplay);
    if (planId !== null && planId !== undefined && planId !== '') {
      localStorage.setItem(STORAGE_KEYS.SPIN_PLAN_ID, String(planId));
    } else {
      localStorage.removeItem(STORAGE_KEYS.SPIN_PLAN_ID);
    }
  } catch (e) {
    console.error('Failed to store spin result:', e);
  }
};

/**
 * Clear spin data (for testing purposes)
 */
export const clearSpinData = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.FINGERPRINT);
    localStorage.removeItem(STORAGE_KEYS.SPIN_COMPLETED);
    localStorage.removeItem(STORAGE_KEYS.SPIN_COUPON);
    localStorage.removeItem(STORAGE_KEYS.SPIN_OFFER);
    localStorage.removeItem(STORAGE_KEYS.SPIN_PLAN_CATEGORY);
    localStorage.removeItem(STORAGE_KEYS.SPIN_PLAN_CATEGORY_DISPLAY);
    localStorage.removeItem(STORAGE_KEYS.SPIN_PLAN_ID);
  } catch (e) {
    console.error('Failed to clear spin data:', e);
  }
};

export default {
  getDeviceFingerprint,
  hasSpunLocally,
  getStoredSpinResult,
  markSpinCompleted,
  clearSpinData,
};
